/**
 * phaseSequencing.js
 * Shared sequencing logic used by BOTH manual and AI-imported projects.
 *
 * Single source of truth for:
 * - canonical AI phase sequence
 * - default duration rules
 * - simultaneous groups
 * - estimated date calculation based on the CURRENT phase order on screen
 */

const DAY = 24 * 60 * 60 * 1000;

export const STANDARD_PHASES = [
  { id: 1, name: 'Hacking', durationDays: 7, aliases: ['hacking'] },

  { id: 2, name: 'Electrical', durationDays: 7, aliases: ['electrical'], groupKey: 'first-mep' },
  { id: 3, name: 'Plumbing', durationDays: 7, aliases: ['plumbing'], groupKey: 'first-mep' },
  { id: 4, name: 'A/C', durationDays: 7, aliases: ['a/c', 'ac', 'aircon', 'air conditioning'], groupKey: 'first-mep' },

  { id: 5, name: 'False Ceiling', durationDays: 5, aliases: ['false ceiling', 'false-ceiling'] },
  { id: 6, name: 'Tiling', durationDays: 7, aliases: ['tiling'] },
  { id: 7, name: 'Throw Debris', durationDays: 1, aliases: ['throw debris', 'debris', 'clearing', 'throwing debris'] },
  { id: 8, name: 'Door Measurement', durationDays: 1, aliases: ['door measurement', 'door management'] },

  { id: 9, name: 'Glass Door/Shower Screen', durationDays: 5, aliases: ['glass door', 'glass door/shower screen', 'shower screen'], groupKey: 'measurement-pack' },
  { id: 10, name: 'Vinyl Measurement', durationDays: 5, aliases: ['vinyl measurement'], groupKey: 'measurement-pack' },
  { id: 11, name: 'Window Measurement', durationDays: 5, aliases: ['window measurement'], groupKey: 'measurement-pack' },

  { id: 12, name: 'Painting', durationDays: 4, aliases: ['painting', 'paint'] },
  { id: 13, name: 'Carpentry Installation', durationDays: 7, aliases: ['carpentry installation', 'carpentry'] },

  { id: 14, name: 'Electrical 2nd Fix', durationDays: 3, aliases: ['electrical 2nd fix', 'electrical second fix'], groupKey: 'second-mep' },
  { id: 15, name: 'Plumbing 2nd Fix', durationDays: 2, aliases: ['plumbing 2nd fix', 'plumbing second fix'], groupKey: 'second-mep' },
  { id: 16, name: 'A/C 2nd Fix', durationDays: 1, aliases: ['a/c 2nd fix', 'ac 2nd fix', 'aircon 2nd fix', 'a/c second fix'], groupKey: 'second-mep' },

  { id: 17, name: 'Cleaning', durationDays: 1, aliases: ['cleaning'] },
  { id: 18, name: 'Vinyl', durationDays: 3, aliases: ['vinyl'] },
  { id: 19, name: 'Marble Polish/Parquet Polish', durationDays: 3, aliases: ['marble polish', 'parquet polish', 'marble polish/parquet polish'] },
];

export const DEFAULT_TOTAL_DURATION_DAYS = STANDARD_PHASES.reduce(
  (sum, phase) => sum + (phase?.durationDays || 0),
  0
);

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

export const toISODate = (date) => {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  return d.toISOString().split('T')[0];
};

export const calcDurationDays = (startDateStr, endDateStr) => {
  if (!startDateStr || !endDateStr) return null;

  const start = new Date(startDateStr);
  const end = new Date(endDateStr);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

  return Math.max(0, Math.round((end.getTime() - start.getTime()) / DAY));
};

export const formatDuration = (days) => {
  if (days == null || isNaN(days)) return null;
  if (days >= 60) return `~${Math.round(days / 30)} months`;
  if (days >= 14) return `~${Math.round(days / 7)} weeks`;
  return `${days} day${days !== 1 ? 's' : ''}`;
};

const normalizeText = (value) => String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();

const getPhaseName = (phase) =>
  normalizeText(phase?.phase_name || phase?.required_trade || phase?.source_task_name || '');

const getStandardMatch = (phase) => {
  const phaseName = getPhaseName(phase);

  return STANDARD_PHASES.find((standard) => {
    const names = [standard?.name, ...(standard?.aliases || [])]
      .map(normalizeText)
      .filter(Boolean);

    return names.some(
      (candidate) =>
        candidate === phaseName ||
        phaseName.includes(candidate) ||
        candidate.includes(phaseName)
    );
  });
};

const getPhaseCost = (phase) => {
  const numeric = Number(phase?.total_cost);
  return Number.isFinite(numeric) ? numeric : 0;
};

const getResolvedDuration = (phase) => {
  const explicit = Number(phase?.durationDays);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;

  const standard = getStandardMatch(phase);
  if (!standard) return 7;

  const cost = getPhaseCost(phase);

  if (standard.name === 'Tiling') {
    return Math.max(7, Math.ceil(cost / 5000) * 7 || 7);
  }

  if (standard.name === 'Carpentry Installation') {
    return Math.max(7, Math.ceil(cost / 10000) * 7 || 7);
  }

  return standard.durationDays || 7;
};

const buildSequencingGroups = (phasesInput) => {
  const sorted = [...phasesInput].sort(
    (a, b) => (Number(a?.phase_order) || 0) - (Number(b?.phase_order) || 0)
  );

  const groups = [];
  let pendingGroup = null;

  sorted.forEach((phase, index) => {
    const standard = getStandardMatch(phase);
    const groupKey = standard?.groupKey || null;
    const duration = getResolvedDuration(phase);

    if (!groupKey) {
      pendingGroup = null;
      groups.push({
        key: `single-${phase?.id || index}`,
        phases: [phase],
        durationDays: duration,
        firstSeenIndex: index,
      });
      return;
    }

    if (!pendingGroup || pendingGroup?.groupKey !== groupKey) {
      pendingGroup = {
        key: `${groupKey}-${index}`,
        groupKey,
        phases: [phase],
        durationDays: duration,
        firstSeenIndex: index,
      };
      groups.push(pendingGroup);
      return;
    }

    pendingGroup.phases.push(phase);
    pendingGroup.durationDays = Math.max(pendingGroup.durationDays, duration);
  });

  return groups;
};

export const sequencePhases = (projectStartDate, phasesInput) => {
  if (!projectStartDate || !phasesInput?.length) return [];

  const startDate = new Date(projectStartDate);
  if (isNaN(startDate.getTime())) return [];

  const groups = buildSequencingGroups(phasesInput);
  let cursor = new Date(startDate);
  const sequenced = [];

  groups.forEach((group) => {
    const duration = group?.durationDays || 7;
    const phaseStart = new Date(cursor);
    const phaseEnd = addDays(phaseStart, duration - 1);

    group?.phases?.forEach((phase) => {
      sequenced.push({
        ...phase,
        estimated_start_date: toISODate(phaseStart),
        estimated_end_date: toISODate(phaseEnd),
        durationDays: getResolvedDuration(phase),
        sequenceBlockDurationDays: duration,
      });
    });

    cursor = addDays(phaseEnd, 1);
  });

  const orderLookup = new Map(
    phasesInput.map((phase, idx) => [phase?.id, Number(phase?.phase_order) || idx + 1])
  );

  return sequenced.map((phase, idx) => ({
    ...phase,
    phase_order: orderLookup.get(phase?.id) || idx + 1,
  }));
};

export const calcProjectEndDate = (projectStartDate, phasesInput) => {
  const sequenced = sequencePhases(projectStartDate, phasesInput);
  if (!sequenced?.length) return null;

  const endDates = sequenced
    .map((phase) => phase?.estimated_end_date)
    .filter(Boolean)
    .sort();

  return endDates.length ? endDates[endDates.length - 1] : null;
};

export const normalizeAiPhaseOrder = (aiPhases) => {
  if (!aiPhases?.length) return [];

  const withMatches = aiPhases.map((phase, idx) => {
    const standard = getStandardMatch(phase);

    return {
      ...phase,
      _standardOrder: standard?.id ?? Number.MAX_SAFE_INTEGER,
      _originalOrder: Number(phase?.phase_order) || idx + 1,
    };
  });

  withMatches.sort((a, b) => {
    if (a._standardOrder !== b._standardOrder) {
      return a._standardOrder - b._standardOrder;
    }

    return a._originalOrder - b._originalOrder;
  });

  return withMatches.map((phase, idx) => ({
    ...phase,
    phase_order: idx + 1,
  }));
};
